<template>
  <wisk />
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import * as Sentry from '@sentry/vue'
import { loadScript, guid, getURLParam, isLocalhost } from '@/modules/utils'
import toaster from '@/modules/toaster'
import { setPreferences, getPreferencesVersion, clearPreferences, getPreferences } from '@/modules/storage'
import { subscribeToWebSettings, updateWebSettingsVersion } from '@/appsync'
import wisk from './Wisk'

const inProduction = import.meta.env.PROD && window.location.host === 'web.wisk.ai'

export default {
  name: 'App',
  components: { wisk },
  data() {
    return {
      initSyncProviderTimeoutId: null,
      checkVenueStatusDebounceId: null,
      inFocus: true,
      visible: true,
      reloading: false,
      shouldReload: false,
      lightWeightPage: false,
    }
  },
  computed: {
    ...mapState(['notification', 'translations', 'waitingForVenue', 'signupFlow', 'bottles', 'pageViewPermissions', 'user']),
    ...mapGetters(['configDataLoaded', 'venue', 'currencies'])
  },
  methods: {
    ...mapActions([
      'changeActiveVenue',
      'getDistributorPrices',
      'getMovementExtraLineReasons',
      'getShoppingCart',
      'setInitialDBLoadingProgress',
      'subscribeToRealtimeSync',
      'setGlobalAction',
      'setTranslations',
      'start'
    ]),
    handleVisibilityChange() {
      this.visible = !document.hidden

      if (this.visible) {
        // reload if another tab switched users or logged out
        if (this.user && (getPreferences('currentUserId') !== this.user.id)) {
          this.reloadPage()
        }
      }
    },
    reloadPage(showConfirmation = false) {
      if (!this.reloading) {
        if (showConfirmation) {
          window.alert(this.translations.confirmAppReload)
          window.location.reload()
        } else {
          window.location.reload()
        }
        this.reloading = true
      }
    },
    checkVenueSubscription() {
      if (this.venue?.id && this.venue?.subscription && this.user && !this.signupFlow) {
        if (this.venue.subscription.status === 'select_plan') {
          this.setGlobalAction({
            type: 'planSelectorEdit',
            preventMultiple: true,
            action: {
              venue: this.venue,
              preventClose: !this.user.god_mode
            }
          })
        }
        if (this.venue.subscription.status === 'add_payment') {
          this.setGlobalAction({
            type: 'addCard',
            preventMultiple: true,
            action: {
              preventClose: !this.user.god_mode,
              venue: this.venue,
              onboarding: true,
              onChange: () => {
                window.location.reload()
              }
            }
          })
        }
        if (this.venue.subscription.status === 'delinquent') {
          this.setGlobalAction({
            type: 'addCard',
            preventMultiple: true,
            action: {
              preventClose: !this.user.god_mode,
              venue: this.venue,
              onChange: () => {
                window.location.reload()
              }
            }
          })

          this.$router.replace({ name: 'subscription', query: this.$route.query }).catch(() => { })
        }
        if (!this.user.god_mode && this.venue.subscription.status === 'churned') {
          this.$router.replace({ name: 'churned' })
        }
      }
    },
    checkVenueStatusDebounced() {
      clearTimeout(this.checkVenueStatusDebounceId)
      this.checkVenueStatusDebounceId = setTimeout(() => {
        if (this.venue?.pos?.status?.type === 'expired') {
          this.setGlobalAction({ type: 'POSEdit', action: { venue: this.venue, id: 1 } })

          this.setGlobalAction({
            type: 'confirmDialog',
            action: {
              type: 'confirm',
              props: {},
              payload: {
                hideOK: true,
                cancelCallback: () => { },
                message: this.translations.txtPOSIntegrationStatusExpiredMessage,
                title: this.translations.txtVenueOperationsPOSType
              }
            }
          })
        }
        if (this.venue.accounting_enabled && this.venue.accounting_integration?.status === 'expired') {
          this.setGlobalAction({ type: 'venueEdit', action: { id: this.venue.id } })

          this.setGlobalAction({
            type: 'confirmDialog',
            action: {
              type: 'confirm',
              props: {},
              payload: {
                hideOK: true,
                cancelCallback: () => { },
                message: this.translations.txtVenueOperationsAccountingStatusExpiredMessage,
                title: this.translations.txtVenueOperationsAccountingSetup
              }
            }
          })
        }
      }, 15000)
    }
  },
  created() {
    this.start()

    //########################################################################
    // IMPORTANT!!!!!!! KEEP THIS IN SYNC WITH FIRESTORE /settings/web/version
    //########################################################################
    window.wiskWebAppVersion = import.meta.env.BUILD_TIMESTAMP //#############
    window.wiskWebAppCOMMITHASH = import.meta.env.COMMITHASH //###############
    //########################################################################
    //########################################################################

    //////////////////////////////
    ///// bump this number in order to reset local storage preferences for all users
    ///// for just one venue set in firestore venue.web_clear_local_preferences = true
    //////////////////////////////
    window.localPreferencesVersion = 10
    //////////////////////////////

    subscribeToWebSettings(result => {
      if (inProduction && window.wiskWebAppVersion && result?.version) {
        const storedVersion = parseInt(result.version, 10)
        if (storedVersion > window.wiskWebAppVersion) {
          console.log('******* this.shouldReload storedVersion > window.wiskWebAppVersion', storedVersion, window.wiskWebAppVersion)
          this.shouldReload = true
        } else if (storedVersion && storedVersion < window.wiskWebAppVersion) {
          updateWebSettingsVersion(window.wiskWebAppVersion.toString())
        }
      }
    }, 600_000) // 10 minutes

    //call this right at app start, before setting any preferences
    let version = getPreferencesVersion()
    if (version < window.localPreferencesVersion) {
      clearPreferences()
    }

    let sessionId = getPreferences('session_id')
    if (!sessionId) {
      sessionId = getURLParam('session_id') || guid()
      setPreferences('session_id', sessionId)
    }

    document.addEventListener('visibilitychange', this.handleVisibilityChange, false)
    window.addEventListener(
      'focus',
      () => {
        this.inFocus = true
        this.handleVisibilityChange()
        if (this.shouldReload) {
          this.reloadPage(true)
        }
      },
      false
    )
    window.addEventListener(
      'blur',
      () => {
        this.inFocus = false
      },
      false
    )
    setTimeout(() => {
      if (getURLParam('import_using_barcodes')) {
        window.WiskGlobals = window.WiskGlobals || {}
        window.WiskGlobals.importUsingBarcodes = true
      }
    }, 1000)
  },
  mounted() {
    if (window.Intercom) {
      window.Intercom('onShow', () => {
        document.body.classList.add('intercom-opened')
      })

      window.Intercom('onHide', () => {
        document.body.classList.remove('intercom-opened')
      })
    }
  },
  beforeUnmount() {
    clearTimeout(this.checkVenueStatusDebounceId)
  },
  watch: {
    currencies() {
      if (this.venue?.display_currency?.code && Array.isArray(this.currencies)) {
        let found = this.currencies.find(c => c.code === this.venue.display_currency.code)
        window.WiskGlobals = window.WiskGlobals || {}
        window.WiskGlobals.currency = (found && found.symbol) || this.venue.display_currency.code
      }
    },
    venue: {
      handler(venue) {
        if (venue?.id) {
          this.checkVenueSubscription()

          if (this.venue?.display_currency?.code && Array.isArray(this.currencies)) {
            let found = this.currencies.find(c => c.code === this.venue.display_currency.code)
            window.WiskGlobals = window.WiskGlobals || {}
            window.WiskGlobals.currency = (found && found.symbol) || this.venue.display_currency.code
          }

          //wait for all venue refresh to complete on first load, it can take up to 15 seconds and we can get many events
          this.checkVenueStatusDebounced()
        }
      },
      immediate: true
    },
    'user.language': {
      handler() {
        this.setTranslations()
      }
    },
    configDataLoaded(configDataLoaded) {
      if (configDataLoaded && !this.signupFlow && !this.lightWeightPage) {
        this.subscribeToRealtimeSync()
      }

      if (!this.user.first_name && !this.user.last_name) {
        this.setGlobalAction({ type: 'userEdit', action: { user: this.user, userId: this.user.id } })
      }

      if (configDataLoaded && !isLocalhost) {
        if (Sentry && Sentry.setUser) {
          Sentry.setUser({
            email: this.user.email,
            id: this.user.id
          })
          Sentry.setExtra('Venue name', this.venue.title)
          Sentry.setExtra('Venue ID', this.venue.id)
          Sentry.setExtra('Wisk version', window.wiskWebAppVersion)
          Sentry.setExtra('COMMITHASH', import.meta.env.COMMITHASH)
        }
      }

      if (configDataLoaded && inProduction && !this.lightWeightPage) {
        let credentials = {
          app_id: import.meta.env.VITE_INTERCOM_APP_ID,
          user_id: this.user.id,
          name: this.user.full_name,
          email: this.user.email,
          company: {
            id: this.venue.id,
            name: this.venue.title
          },
          user_hash: this.user?.intercom_security_data?.web_hash || '',
          hide_default_launcher: false,
          custom_launcher_selector: '.help-launcher'
        }

        this.$intercom.boot(credentials)

        if (this.$route.query.chat) {
          this.$intercom.show()
        }

        loadScript(
          'https://js.cobrowse.io/CobrowseIO.js', {
          async: false,
          id: 'wisk-cobrowse-script',
          callback: () => {
            setTimeout(() => {
              if (window.CobrowseIO && this.user) {
                window.CobrowseIO.license = 'GSZ4mD0ob8WmFg'
                window.CobrowseIO.customData = {
                  venue_name: this.venue.title,
                  user_id: this.user.id,
                  name: this.user.full_name,
                  email: this.user.email,
                  venue_id: this.venue.id,
                  device_name: 'Web - ' + this.user.full_name + ' ' + this.user.email + ' ' + this.venue.title
                }
                window.CobrowseIO.start()
              }
            }, 0)
          }
        })
        loadScript(
          'https://feedback.wisk.ai/javascript/upvoty.embed.js', {
          id: 'wisk-upvoty-script',
          callback: () => {
            setTimeout(() => {
              if (window.upvoty && this.user) {
                window.upvoty.init('identify', {
                  user: {
                    id: this.user.id,
                    name: this.user.full_name + (this.user.venue ? ' - ' + this.user.venue.title : ''),
                    email: this.user.email,
                  },
                  baseUrl: 'feedback.wisk.ai',
                  publicKey: import.meta.env.VITE_UPVOTY_PUBLIC_KEY,
                })
              }
            }, 0)
          }
        })

        if (window.profitwell) {
          window.profitwell('user_email', this.user.id.toString())
          window.profitwell('user_id', (this.venue.stripe_id || '').toString())
        }
      }
    },
    pageViewPermissions: {
      handler(pageViewPermissions) {
        if (pageViewPermissions && pageViewPermissions.initDone && this.$route.name && !pageViewPermissions[this.$route.name] && !this.lightWeightPage) {
          let query = { ...this.$route.query }

          this.$router.replace({ name: 'home', query }).catch(() => { })
        }
      },
      immediate: true
    },
    user: {
      handler() {
        if (this.user) {
          document.body.classList.add('user-logged-in')
          this.checkVenueSubscription()
        } else {
          document.body.classList.remove('user-logged-in')
        }
      },
      immediate: true
    },
    $route: {
      deep: false,
      handler(to) {
        this.lightWeightPage = to.meta.lightWeightPage

        if (this.lightWeightPage) {
          this.setInitialDBLoadingProgress(null)
        }
        if (!this.signupFlow) {
          if (this.shouldReload) {
            this.reloadPage()
          }
          if (this.user && this.pageViewPermissions.initDone && !this.pageViewPermissions[to.name] && !this.lightWeightPage) {
            let query = { ...this.$route.query }
            console.log('*/*/*/* REDIRECT back to dashboard because missing permission to access the view', to.name)
            this.$router.replace({ name: 'home', query }).catch(() => { })
          }
        }
      }
    },
    notification(notification) {
      if (notification) {
        if (notification.type === 'makeToast') {
          toaster.makeToast(notification)
        } else {
          toaster[notification.type](notification.message)
        }
      }
    }
  }
}
</script>

<style lang="scss"></style>
